import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { TransactionDetails } from '../../../types/api.types';
import './DiscountOptions.scss';
import { CardElement } from '../CardElement/CardElement';
import {
  getDiscountBanksAPI,
  applyDiscountAPI,
  resetDiscountAPI,
} from '../../../services/installment.service';
import { Loader } from '../../../pages/Loader/Loader';
import { SET_PAYMENT_DETAILS } from '../../../redux/paymentActions';
import { toast } from 'react-toastify';

export interface DiscountOptionsProps {
  details: TransactionDetails;
  response: any;
  onStepChange?: (step: string, data?: any) => void;
  onBackNavigation?: () => boolean;
}

interface DiscountBank {
  bank_id: number;
  bank_name: string;
  bank_official_name: string;
  bank_logo?: string; // Make optional as API might not always provide this
  discount_id: string;
  discount_percentage: number;
  amount_before_discount: number;
  amount_after_discount: number;
  bins?: string[]; // Optional array of BIN numbers for validation
  payment_method: 'VISA' | 'MASTERCARD' | 'AMEX';
  card_type: 'DEBIT' | 'CREDIT';
}

interface DiscountApplyResponse {
  status: number;
  message: string;
  data: {
    discount_id: string;
    discount_percentage: string;
    total_payable_amount: number;
    discount_amount: number;
    bank_bins: string[];
  };
}

interface DiscountResetResponse {
  status: number;
  message: string;
  data: {
    onepay_transaction_id: string;
    request_amount: string;
    total_payable_amount: number;
    is_discount: boolean;
    charge: string;
    net_amount: string;
  };
}

// Helper function to format card type label
const getCardTypeLabel = (cardType: 'DEBIT' | 'CREDIT'): string => {
  const labels = {
    'DEBIT': 'Debit Card',
    'CREDIT': 'Credit Card',
  };
  return labels[cardType] || cardType;
};

// Helper function to format payment method label
const getPaymentMethodLabel = (paymentMethod: 'VISA' | 'MASTERCARD' | 'AMEX'): string => {
  const labels = {
    'VISA': 'Visa',
    'MASTERCARD': 'Mastercard',
    'AMEX': 'Amex',
  };
  return labels[paymentMethod] || paymentMethod;
};

export const DiscountOptions = ({
  details,
  response,
  onStepChange,
  onBackNavigation,
}: DiscountOptionsProps) => {
  const [selectedBank, setSelectedBank] = useState<DiscountBank | null>(null);
  const [discountBanks, setDiscountBanks] = useState<DiscountBank[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showOtherCard, setShowOtherCard] = useState(false);
  const [showCardElement, setShowCardElement] = useState(false);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [originalAmount] = useState(details.data.request_amount); // Store original amount when component loads
  const dispatch = useDispatch();
  const isInitialMount = useRef(true);

  // Fetch discount banks when component mounts
  useEffect(() => {
    const fetchDiscountBanks = async () => {
      setIsLoading(true);
      try {
        const response = await getDiscountBanksAPI(
          details.data.onepay_transaction_id
        );
        if (response && response.status === 200) {
          setDiscountBanks(response.data);
        }
      } catch (error) {
        console.error('Error fetching discount banks:', error);
        toast.error('Failed to load discount options');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDiscountBanks();
  }, [details.data.onepay_transaction_id]);

  // Notify parent about current step whenever state changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (onStepChange) {
      if (selectedBank) {
        onStepChange('bank-selected', { selectedBank });
      } else if (showOtherCard) {
        onStepChange('other-card-selected', {});
      } else {
        onStepChange('root', {});
      }
    }
  }, [selectedBank, showOtherCard, onStepChange]);

  // Handle back navigation from parent
  useEffect(() => {
    if (onBackNavigation) {
      const handleBack = () => {
        if (showCardElement) {
          // If showing card element, go back to selection and reset states
          setShowCardElement(false);
          setSelectedBank(null);
          setShowOtherCard(false);

          // Reset discount in Redux when going back from card element
          dispatch({
            type: SET_PAYMENT_DETAILS,
            payload: {
              data: {
                ...details.data,
                request_amount: originalAmount,
                discount_applied: undefined,
              },
            },
          });

          return true;
        } else if (selectedBank || showOtherCard) {
          // If selection made, reset to initial state
          setSelectedBank(null);
          setShowOtherCard(false);

          // Reset discount in Redux when going back from selection
          dispatch({
            type: SET_PAYMENT_DETAILS,
            payload: {
              data: {
                ...details.data,
                request_amount: originalAmount,
                discount_applied: undefined,
              },
            },
          });

          return true;
        }
        return false;
      };

      (window as any).cardPaymentBackHandler = handleBack;
    }
  }, [
    selectedBank,
    showOtherCard,
    showCardElement,
    onBackNavigation,
    dispatch,
    details.data,
    originalAmount,
  ]);

  const handleBankSelection = async (bank: DiscountBank) => {
    setIsApplyingDiscount(true);

    try {
      // Call the apply discount API
      const applyResponse: DiscountApplyResponse = await applyDiscountAPI(
        details.data.onepay_transaction_id,
        bank.discount_id
      );
      console.log(applyResponse, 'this is appply response');
      if (applyResponse && applyResponse.status === 200) {
        setSelectedBank(bank);

        // Update the payment details with the API response data
        dispatch({
          type: SET_PAYMENT_DETAILS,
          payload: {
            data: {
              ...details.data,
              request_amount:
                applyResponse.data.total_payable_amount.toString(),
              discount_applied: {
                bank_id: bank.bank_id,
                discount_id: applyResponse.data.discount_id,
                discount_percentage: parseFloat(
                  applyResponse.data.discount_percentage
                ),
                original_amount: parseFloat(originalAmount),
                discounted_amount: applyResponse.data.total_payable_amount,
                discount_amount: applyResponse.data.discount_amount,
                bank_bins: applyResponse.data.bank_bins,
              },
            },
          },
        });

        // Show success message
        // toast.success(
        //   applyResponse.message || 'Discount applied successfully!'
        // );

        // Show card element after successful API call
        setShowCardElement(true);
      } else {
        toast.error(applyResponse?.message || 'Failed to apply discount');
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      toast.error('Failed to apply discount. Please try again.');
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  const handleOtherCardSelection = async () => {
    setIsApplyingDiscount(true);

    try {
      // Call the reset discount API
      const resetResponse: DiscountResetResponse = await resetDiscountAPI(
        details.data.onepay_transaction_id
      );

      if (resetResponse && resetResponse.status === 200) {
        setShowOtherCard(true);

        // Update the payment details with the API response data
        dispatch({
          type: SET_PAYMENT_DETAILS,
          payload: {
            data: {
              ...details.data,
              request_amount:
                resetResponse.data.total_payable_amount.toString(),
              discount_applied: undefined,
            },
          },
        });

        // Show success message
        // toast.success(
        //   resetResponse.message || 'Discount mode reset successfully!'
        // );

        // Show card element after successful API call
        setShowCardElement(true);
      } else {
        toast.error(resetResponse?.message || 'Failed to reset discount mode');
      }
    } catch (error) {
      console.error('Error resetting discount:', error);
      toast.error('Failed to reset discount mode. Please try again.');
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  return (
    <div>
      {/* Show loading indicator when applying discount */}
      {isApplyingDiscount && (
        <div className="text-center p-3">
          <Loader />
          <div className="mt-2">
            {selectedBank
              ? 'Applying discount...'
              : 'Resetting discount mode...'}
          </div>
        </div>
      )}

      {/* Show discount banks and other card option if no selection made and not loading */}
      {!selectedBank &&
        !showOtherCard &&
        !showCardElement &&
        !isApplyingDiscount && (
          <div>
            <div className="discount-option mb-4">
              Bank Card <span>Select your bank to get discount</span>
            </div>

            {isLoading && <Loader />}

            {/* Display discount banks */}
            {!isLoading &&
              discountBanks.map((bank) => (
                <div
                  key={bank.bank_id}
                  onClick={() => handleBankSelection(bank)}
                  className="discount-bank-card"
                >
                  <div className="d-flex align-items-center">
                    {bank.bank_logo ? (
                      <img
                        src={bank.bank_logo}
                        alt={bank.bank_name}
                        className="bank-logo"
                      />
                    ) : (
                      <div className="bank-logo-placeholder">
                        {bank.bank_name.charAt(0)}
                      </div>
                    )}
                    <div className="ms-3">
                      <div className="bank-name">{bank.bank_official_name}</div>
                      <div className="debit-card-tag">
                        {getCardTypeLabel(bank.card_type)} - {getPaymentMethodLabel(bank.payment_method)}
                      </div>
                    </div>
                  </div>
                  <div className="discount-info">
                    <div className="discount-percentage">
                      {bank.discount_percentage}% OFF
                    </div>
                    <div className="discounted-amount">
                      Rs. {bank.amount_after_discount.toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}

            {/* Other Card option at the end */}
            {!isLoading && (
              <div
                onClick={handleOtherCardSelection}
                className="discount-bank-card"
              >
                <div className="d-flex align-items-center">
                  <div>
                    <div className="bank-name">Other Card</div>
                    <div
                      className="debit-card-tag"
                      style={{ background: '#6c757d' }}
                    >
                      NO DISCOUNT
                    </div>
                  </div>
                </div>
                <div className="discount-info">
                  <div
                    className="discount-percentage"
                    style={{ color: '#6c757d' }}
                  >
                    NO DISCOUNT
                  </div>
                  <div className="discounted-amount">Rs. {originalAmount}</div>
                </div>
              </div>
            )}
          </div>
        )}

      {/* Show CardElement only after successful API call */}
      {showCardElement && <CardElement response={response} />}
    </div>
  );
};
